import React, { useState } from 'react';
import { MessageCircle, X, Send, Loader2 } from 'lucide-react';

const ChatWidget = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');

    try {
      const response = await fetch('https://api.web3forms.com/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          access_key: '0b02448c-3329-4d04-ba3d-656625a4a6dc',
          subject: `New Chat Message from ${formData.name}`,
          from_name: 'InArch Design Studio Website',
          to: 'info@inarchdesignstudio.com',
          name: formData.name,
          phone: formData.phone,
          email: formData.email,
          message: formData.message,
        }),
      });

      const result = await response.json();

      if (result.success) {
        setIsSubmitted(true);
        setFormData({ name: '', phone: '', email: '', message: '' });
        setTimeout(() => {
          setIsSubmitted(false);
        }, 5000);
      } else {
        setError('Failed to send message. Please try again.');
      }
    } catch (err) {
      setError('Failed to send message. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      {/* Chat Bubble Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`fixed bottom-6 right-6 z-50 w-14 h-14 rounded-full shadow-lg flex items-center justify-center transition-all duration-300 ${
          isOpen 
            ? 'bg-[#2C2C2C] rotate-0' 
            : 'bg-[#B87333] hover:bg-[#8B5A2B] hover:scale-110'
        }`}
        aria-label="Chat with us"
      >
        {isOpen ? (
          <X size={24} className="text-white" />
        ) : (
          <MessageCircle size={24} className="text-white" />
        )}
      </button>

      {/* Chat Box */}
      <div
        className={`fixed bottom-24 right-6 z-50 w-80 bg-white rounded-lg shadow-2xl transition-all duration-300 overflow-hidden ${
          isOpen 
            ? 'opacity-100 translate-y-0 pointer-events-auto' 
            : 'opacity-0 translate-y-4 pointer-events-none'
        }`}
      >
        {/* Header */}
        <div className="bg-[#B87333] px-4 py-4">
          <h3 className="text-white font-serif text-lg">Chat with Us</h3>
          <p className="text-white/80 text-xs">We typically reply within minutes</p>
        </div>

        {/* Body */}
        <div className="p-4">
          {isSubmitted ? (
            <div className="text-center py-8">
              <div className="w-12 h-12 mx-auto flex items-center justify-center bg-green-100 rounded-full mb-4">
                <Send size={20} className="text-green-600" />
              </div>
              <h4 className="font-serif text-lg text-[#2C2C2C] mb-2">Message Sent!</h4>
              <p className="text-[#6B6B6B] text-sm">We'll get back to you soon.</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-3">
              <div>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  placeholder="Your Name *"
                  className="w-full px-3 py-2 text-sm border border-[#E8E3DB] rounded focus:border-[#B87333] focus:ring-1 focus:ring-[#B87333] outline-none transition-colors"
                />
              </div>
              <div>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  required
                  placeholder="Phone Number *"
                  className="w-full px-3 py-2 text-sm border border-[#E8E3DB] rounded focus:border-[#B87333] focus:ring-1 focus:ring-[#B87333] outline-none transition-colors"
                />
              </div>
              <div>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  placeholder="Email Address *"
                  className="w-full px-3 py-2 text-sm border border-[#E8E3DB] rounded focus:border-[#B87333] focus:ring-1 focus:ring-[#B87333] outline-none transition-colors"
                />
              </div>
              <div>
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  rows={3}
                  placeholder="Your Message *"
                  className="w-full px-3 py-2 text-sm border border-[#E8E3DB] rounded focus:border-[#B87333] focus:ring-1 focus:ring-[#B87333] outline-none transition-colors resize-none"
                />
              </div>

              {error && (
                <p className="text-red-500 text-xs">{error}</p>
              )}

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full py-2.5 bg-[#B87333] hover:bg-[#8B5A2B] text-white text-sm font-medium rounded transition-colors disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 size={16} className="mr-2 animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    <Send size={16} className="mr-2" />
                    Send Message
                  </>
                )}
              </button>
            </form>
          )}
        </div>

        {/* Footer */}
        <div className="px-4 py-2 bg-[#F5F0E8] border-t border-[#E8E3DB]">
          <p className="text-[#6B6B6B] text-xs text-center">
            Or call us at <a href="tel:+918591613610" className="text-[#B87333] font-medium">+91 8591613610</a>
          </p>
        </div>
      </div>
    </>
  );
};

export default ChatWidget;
